import { type JSX, type ReactNode, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { EYEBROW } from "@/lib/class-presets";
import {
  AGENT_SHORTCUTS,
  SHORTCUT_REMAP_NOTE,
  agentShortcutSection,
  globalShortcuts,
} from "@/lib/navigation";

interface NavigateTo {
  (path: string): void;
}

export interface KeyboardShortcutState {
  readonly helpOpen: boolean;
  closeHelp(): void;
  openHelp(): void;
}

interface ShortcutEntry {
  readonly keys: string;
  readonly label: string;
}

function typingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return (
    target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])') !==
    null
  );
}

function agentBase(pathname: string): string | undefined {
  const match = /^\/agents\/([^/]+)/.exec(pathname);
  return match?.[1] === undefined ? undefined : `/agents/${match[1]}`;
}

/**
 * The agent the user is on, as of this keystroke.
 *
 * `window.location` is the authoritative answer in the browser: React Router
 * pushes history synchronously inside `navigate()`, but wraps its own state
 * update in `startTransition`, and React holds that transition back while the
 * next page's lazy chunk loads. For that window the committed `pathname` still
 * names the previous route — which is how Alt+W went nowhere immediately after
 * opening an agent. Reading the live path closes the window.
 *
 * Authoritative means authoritative in both directions: when the live URL is
 * not an agent route the answer is "no agent", never the committed path. The
 * same pending window runs the other way — leaving an agent for /account — and
 * falling back there would send Alt+Q right back into the agent just left.
 *
 * The committed `pathname` is read only where there is no browser URL at all,
 * which is `MemoryRouter` in tests and any future in-memory host.
 */
function currentAgentBase(pathname: string): string | undefined {
  if (typeof window === "undefined") return agentBase(pathname);
  return agentBase(window.location.pathname);
}

export function useKeyboardShortcuts(
  navigate: NavigateTo,
  pathname: string,
  admin: boolean,
): KeyboardShortcutState {
  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(
    function registerKeyboardShortcuts(): () => void {
      function keyDown(event: KeyboardEvent): void {
        if (typingTarget(event.target)) return;
        if (event.key === "?") {
          event.preventDefault();
          setHelpOpen(function toggleHelp(open): boolean {
            return !open;
          });
          return;
        }
        if (!event.altKey || event.ctrlKey || event.metaKey) return;
        const normalized = event.key.toLowerCase();
        const destinationIndex = /^\d$/.test(normalized) ? Number(normalized) - 1 : -1;
        // Admin-only destinations are only in the admin table, so a familiar
        // key can never reach an administrative page without the role (§8).
        const destination = globalShortcuts(admin)[destinationIndex];
        if (destination !== undefined) {
          event.preventDefault();
          navigate(destination.to);
          return;
        }
        const section = agentShortcutSection(normalized);
        const base = currentAgentBase(pathname);
        if (section !== undefined && base !== undefined) {
          event.preventDefault();
          navigate(`${base}/${section}`);
        }
      }
      window.addEventListener("keydown", keyDown);
      return function unregisterKeyboardShortcuts(): void {
        window.removeEventListener("keydown", keyDown);
      };
    },
    [admin, navigate, pathname],
  );
  function closeHelp(): void {
    setHelpOpen(false);
  }
  function openHelp(): void {
    setHelpOpen(true);
  }
  return { helpOpen, closeHelp, openHelp };
}

const SHORTCUT_ROW = "grid grid-cols-[72px_minmax(0,1fr)] gap-2 border-t pt-2";

/**
 * Both the handler and this overlay read the one shortcut table in
 * `@/lib/navigation`, so a documented key can no longer disagree with the key
 * that actually fires — the overlay used to label Alt+4 "Users" while the
 * handler navigated to `/settings`.
 */
const AGENT_ENTRIES: readonly ShortcutEntry[] = AGENT_SHORTCUTS.map(
  function agentEntry(binding): ShortcutEntry {
    return { keys: `Alt+${binding.key.toUpperCase()}`, label: binding.label };
  },
);

export function ShortcutsOverlay({
  admin,
  onClose,
  open,
}: {
  readonly admin: boolean;
  readonly onClose: () => void;
  readonly open: boolean;
}): JSX.Element | null {
  const closeButton = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  useEffect(
    function manageOverlayFocus(): (() => void) | undefined {
      if (!open) return undefined;
      previousFocus.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      closeButton.current?.focus();
      return function restoreOverlayFocus(): void {
        previousFocus.current?.focus();
        previousFocus.current = null;
      };
    },
    [open],
  );
  useEffect(
    function registerOverlayEscape(): (() => void) | undefined {
      if (!open) return undefined;
      function escapeOverlay(event: KeyboardEvent): void {
        if (event.key !== "Escape") return;
        event.preventDefault();
        onClose();
      }
      window.addEventListener("keydown", escapeOverlay);
      return function unregisterOverlayEscape(): void {
        window.removeEventListener("keydown", escapeOverlay);
      };
    },
    [onClose, open],
  );
  if (!open) return null;
  const entries = [...globalShortcuts(admin), ...AGENT_ENTRIES];
  return (
    <div
      aria-labelledby="shortcuts-title"
      aria-modal="true"
      className="fixed inset-0 z-40 grid place-items-center bg-foreground/45 p-5"
      role="dialog"
    >
      <section className="grid w-[min(100%,560px)] gap-4 border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className={EYEBROW}>Keyboard navigation</p>
            <h2 id="shortcuts-title">Shortcuts</h2>
          </div>
          <Button onClick={onClose} ref={closeButton} variant="outline">
            Close
          </Button>
        </div>
        <dl className="grid gap-2 md:grid-cols-2 md:gap-x-5">
          <div className={SHORTCUT_ROW}>
            <dt className="font-semibold text-brand">?</dt>
            <dd className="text-muted-foreground">Show or hide this help</dd>
          </div>
          {entries.map(function shortcutRow(entry): ReactNode {
            return (
              <div className={SHORTCUT_ROW} key={entry.keys}>
                <dt className="font-semibold text-brand">{entry.keys}</dt>
                <dd className="text-muted-foreground">{entry.label}</dd>
              </div>
            );
          })}
        </dl>
        <p className="text-muted-foreground">
          Shortcuts pause while focus is in an editable control.
        </p>
        {/* A key that changed says so, rather than silently going elsewhere (§8). */}
        {admin && <p className="text-muted-foreground">{SHORTCUT_REMAP_NOTE}</p>}
      </section>
    </div>
  );
}
